import { Checkbox, Typography } from 'antd';
import Button from 'components/ui/base/button';
import Container from 'components/ui/base/container';
import Divider from 'components/ui/base/divider';
import Drawer from 'components/ui/base/drawer/Drawer';
import CustomModal from 'components/ui/base/modal/Modal';
import SearchBar from 'components/ui/base/searchbar';
import { H5 } from 'components/ui/base/typography';
import { defaultPagination, PaginatedParams } from 'core/http/pagination';
import useSession from 'hooks/useSession';
import debounce from 'lodash.debounce';
import { ChangeEventHandler, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { deleteIndividual, editIndividual, listIndividuals } from 'store/individuals/client';
import {
    brokerIndividualTypeLabel,
    driverIndividualTypeLabel,
    freightForwarderIndividualTypeLabels,
    GetIndividualsParams,
    Individual,
    IndividualType,
    traderIndividualTypeLabel,
} from 'store/individuals/individual';
import { SelectOption } from '../../components/ui/composed/formSelect/FormSelect';
import { getTableChangeParams, TableChangeParams } from '../../utils/tableHelpers';
import CreateNewIndividual from './components/CreateNewIndividual';
import IndividualDetails from './components/IndividualDetails';
import IndividualsTable from './components/IndividualsTable';
import { resendInvite } from 'store/session/client';
import FilterDropdown from 'components/ui/composed/filter/FilterDropdown';
import { SpaceBetween } from 'components/styles/layout.styles';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import axiosClient from '../../config/axios';
import FileExcelOutlined from '@ant-design/icons/lib/icons/FileExcelOutlined';
import { useTranslation } from 'react-i18next';
import { DivDownloadTemplate } from './components/IndividualDetails.styles';
import { uploadFile } from 'utils/uploadFile';
import IndividualsImportErrorsDrawer from './IndividualsImportErrorsDrawer';
import { ErrorResponse } from 'core/http/response';
import { useRequestPromise } from 'hooks/useRequest';
import { SegmentedValue } from 'antd/lib/segmented';
import ConfirmTeamRoleChangeModal from 'views/teams/components/ConfirmTeamRoleChangeModal';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import { StyledSegmented } from 'components/new-ui/base/segmented/styles';

const { Text: AntText } = Typography;

const ROLES_REQUIRING_CONFIRMATION: IndividualType[] = [
    IndividualType.CLEARANCE_AGENT,
    IndividualType.JOB_MANAGER,
    IndividualType.TEAM_MANAGER,
];

const UserManagement: FC = () => {
    const { userInfo } = useSession();
    const { t } = useTranslation('customs_declarations');

    const [showUserDetails, setShowUserDetails] = useState(false);
    const [individual, setIndividual] = useState<Individual>();
    const [showAddNewUserDrawer, setShowAddNedUserDrawer] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteIds, setDeleteIds] = useState<string[]>([]);
    const [importErrors, setImportErrors] = useState<ErrorResponse | undefined>(undefined);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [showRoleConfirm, setShowRoleConfirm] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState<{
        id: string;
        role: IndividualType;
    } | null>(null);

    const [paginator, setPaginator] = useState<Partial<PaginatedParams>>(defaultPagination);
    const [activeFilter, setActiveFilter] = useState<'active' | 'inactive'>('active');
    const [userTypes, setUserTypes] = useState<IndividualType[]>([]);
    const [query, setQuery] = useState('');

    // For now, we don't set the filters to the URL params as we're not displaying the active filters
    // This page is planned to be reworked as there is a new design
    const appliedFilters: Partial<GetIndividualsParams> = useMemo(
        () => ({
            active: activeFilter === 'active' ? true : false,
            page: paginator.page,
            size: paginator.size,
            type: userTypes,
            query,
        }),
        [activeFilter, paginator.page, paginator.size, userTypes, query]
    );

    const {
        data: individuals,
        refetch,
        isLoading,
    } = useRequestPromise(() => listIndividuals(appliedFilters), {}, [appliedFilters]);

    const onSearch = useCallback((value: string) => {
        setQuery(value);
        setPaginator((prev) => ({ ...prev, page: 0 }));
    }, []);

    const debouncedSearch = useMemo(() => debounce((value: string) => onSearch(value), 500), [onSearch]);

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    useEffect(() => {
        setImportErrors(undefined);
        setShowErrorModal(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getUser = useCallback(
        (id: string) => individuals?.list.find((element) => element.id === id),
        [individuals?.list]
    );

    useEffect(() => {
        if (!individuals?.list.length && !individuals?.pageSize && isLoading !== false) {
            refetch();
        }
        document.getElementById('import')?.addEventListener('change', upload as any);
        return () => document.getElementById('import')?.removeEventListener('change', upload as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individuals, isLoading]);

    const handleDelete = (ids: string[]) => {
        setDeleteIds(ids);
        setDeleteModalVisible(true);
    };

    const handleResendInvite = async (id: string) => {
        try {
            await resendInvite(id);
            Notification({
                type: 'success',
                messageTitle: 'Invitation Sent',
                description: "We've resent the invitation. The recipient should receive it shortly.",
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                type: 'error',
                messageTitle: 'Resend Failed',
                description: errorMessage || 'We were unable to resend the invitation. Please try again in a moment.',
            });
        }
    };

    const handleEdit = (id: string) => {
        const user = getUser(id);
        setIndividual(user);
        setShowAddNedUserDrawer(true);
    };

    const onIndividualsTableChange = (params: TableChangeParams) => {
        setPaginator((prev) => ({
            ...prev,
            ...getTableChangeParams({ pagination: params.pagination, sorter: params.sorter }),
        }));
        refetch();
    };

    const handleUpdateRole = async (role: IndividualType, id: string) => {
        const user = getUser(id);
        if (!user) return;

        const currentRole = user.type;

        if (currentRole === role) return;

        // Request confirmation when the user has a team role and we want to change it
        if (ROLES_REQUIRING_CONFIRMATION.includes(currentRole)) {
            setPendingRoleChange({ id, role });
            setShowRoleConfirm(true);
            return;
        }

        await editIndividual(id, { ...user, type: role })
            .then(() => {
                Notification({
                    type: 'success',
                    messageTitle: 'Role Updated',
                    description: "The user's role has been updated successfully.",
                });
                refetch();
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage || 'We were unable to update the user’s role. Please try again.',
                });
            });
    };

    const handleUpdateStatus = async (status: string, id: string) => {
        const user = getUser(id);
        const _status = status === 'Active' ? true : false;

        await editIndividual(id, { ...user, active: _status })
            .then(() => {
                Notification({
                    type: 'success',
                    messageTitle: 'Status Updated',
                    description: "The user's status has been updated successfully.",
                });
                refetch();
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Update Failed',
                    description: errorMessage || "We were unable to update the user's status. Please try again.",
                });
            });
    };

    const handleDetails = (id: string) => {
        const user = getUser(id);
        setIndividual(user);
        setShowUserDetails(true);
    };

    const clearIndividualData = () => {
        setIndividual(undefined);
        setShowAddNedUserDrawer(false);
    };

    const clearIndividualDetailsData = () => {
        setIndividual(undefined);
        setShowUserDetails(false);
    };

    const deleteIndividuals = () => {
        setDeleteModalVisible(false);

        Promise.all(deleteIds.map((id) => deleteIndividual(id)))
            .then(() => {
                const multiple = deleteIds.length > 1;
                Notification({
                    type: 'success',
                    messageTitle: multiple ? 'Users Deleted' : 'User Deleted',
                    description: multiple
                        ? `${deleteIds.length} users have been deleted successfully.`
                        : 'User has been deleted successfully.',
                });

                refetch();
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Delete Failed',
                    description: errorMessage || 'We were unable to delete the selected user(s). Please try again.',
                });
            });
    };

    const roleSelectOptions: Readonly<SelectOption[]> | undefined = useMemo(() => {
        switch (userInfo?.role) {
            case IndividualType.TRADER_ADMIN:
            case IndividualType.TRADER_USER:
                return traderIndividualTypeLabel;
            case IndividualType.DRIVER:
            case IndividualType.HAULIER_ADMIN:
                return driverIndividualTypeLabel;
            case IndividualType.BROKER_ADMIN:
            case IndividualType.BROKER_CLERK:
                return brokerIndividualTypeLabel;
            case IndividualType.FREIGHT_FORWARDER_ADMIN:
            case IndividualType.TEAM_MANAGER:
            case IndividualType.JOB_MANAGER:
            case IndividualType.CLEARANCE_AGENT:
            case IndividualType.ACCOUNTS_MANAGER:
                return freightForwarderIndividualTypeLabels;
            default:
                Error('Invalid individual type');
        }
    }, [userInfo?.role]);

    const handleStatusChange = (value: SegmentedValue) => {
        setActiveFilter(value === 'active' ? 'active' : 'inactive');
        setPaginator((prev) => ({ ...prev, page: 0 }));
    };

    const upload: ChangeEventHandler<HTMLInputElement> = async (e) => {
        const files = Array.from(e.target.files as unknown as File[]);
        const file = files[0];

        const preSignUrlPayload = {
            fileName: file.name,
        };

        try {
            const data = await axiosClient
                .get('/cms/individuals/csv/upload', { params: preSignUrlPayload })
                .then((res) => res.data.payload);

            await uploadFile(file, data);

            const match = data.match(/\/([^/?]+)\?/);
            const uuid = match?.[1];

            await axiosClient.post(`/cms/individuals/import?s3Key=${uuid}`);

            refetch();

            Notification({
                type: 'success',
                messageTitle: 'Import Complete',
                description: 'Your file was processed successfully and users were imported.',
            });
        } catch (error: any) {
            if (error.response && error.response.data) {
                const responseData = error.response.data;
                setImportErrors(responseData);
                setShowErrorModal(true);
            } else {
                const errorMessage = getErrorMessage(error);
                Notification({
                    type: 'error',
                    messageTitle: 'Import Failed',
                    description:
                        errorMessage || 'We could not process the file. Please try again or upload a valid template.',
                });
            }
        } finally {
            e.target.value = '';
        }
    };

    const handleDownloadUsers = async () => {
        try {
            const data = await axiosClient.get(`/cms/individuals/export/url`).then((res) => res.data.payload);
            window.open(data);
        } catch (error) {}
    };

    const handleDownloadSpreadsheet = async () => {
        try {
            const data = await axiosClient.get(`/cms/individuals/downloadTemplate`).then((res) => res.data.payload);
            window.open(data);
        } catch (error) {}
    };

    const handleImport = () => {
        document.getElementById('import')?.click();
    };

    const closeModal = () => {
        setShowErrorModal(false);
    };

    return (
        <>
            <Drawer
                title={!individual ? 'Add New User' : 'Edit User'}
                width={627}
                open={showAddNewUserDrawer}
                onClose={clearIndividualData}
            >
                <CreateNewIndividual
                    closeDrawer={clearIndividualData}
                    individual={individual}
                    registeredUsers={individuals?.list}
                    onSuccess={refetch}
                    roleSelectOptions={roleSelectOptions}
                />
            </Drawer>

            <IndividualsImportErrorsDrawer
                individualImportError={importErrors}
                open={showErrorModal}
                onClose={closeModal}
            />

            <Container style={{ width: 'max-content' }}>
                <DivDownloadTemplate>
                    <AntText style={{ color: 'inherit' }} onClick={() => handleDownloadSpreadsheet()}>
                        <FileExcelOutlined /> {t('bulk-upload.download-spreadsheet-template-link')}
                    </AntText>
                </DivDownloadTemplate>
                <H5>User Management</H5>
                <SpaceBetween style={{ marginTop: '1.6rem' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <Button size="large" type="primary" onClick={() => setShowAddNedUserDrawer(true)}>
                            Add New User
                        </Button>
                        <Button size="large" type="primary" icon={<UploadOutlined />} onClick={handleImport}>
                            Import Users
                            <input id="import" type="file" hidden />
                        </Button>
                        <Button
                            size="large"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadUsers();
                            }}
                        >
                            Export Users
                        </Button>
                    </div>
                    <StyledSegmented
                        value={activeFilter}
                        onChange={handleStatusChange}
                        options={[
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                        ]}
                    />
                </SpaceBetween>
                <Divider />
                <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                    <SearchBar
                        onSearch={(value) => debouncedSearch(value)}
                        inputPlaceholder="Search by name, role, email and phone number"
                        onClear={() => {
                            setQuery('');
                            setPaginator((prev) => ({ ...prev, page: 0 }));
                        }}
                        style={{ flexGrow: 1 }}
                    />
                    <FilterDropdown
                        filters={() => [
                            {
                                label: 'Role',
                                content: (
                                    <Checkbox.Group
                                        style={{ display: 'flex', flexDirection: 'column' }}
                                        options={[
                                            { label: 'Broker Admin', value: IndividualType.BROKER_ADMIN },
                                            { label: 'Broker Clerk', value: IndividualType.BROKER_CLERK },
                                            { label: 'Trader Admin', value: IndividualType.TRADER_ADMIN },
                                            { label: 'Trader User', value: IndividualType.TRADER_USER },
                                            { label: 'Haulier Admin', value: IndividualType.HAULIER_ADMIN },
                                            { label: 'Driver', value: IndividualType.DRIVER },
                                            {
                                                label: 'Freight Forwarder Admin',
                                                value: IndividualType.FREIGHT_FORWARDER_ADMIN,
                                            },
                                            { label: 'Accounts Manager', value: IndividualType.ACCOUNTS_MANAGER },
                                            { label: 'Team Manager', value: IndividualType.TEAM_MANAGER },
                                            { label: 'Job Manager', value: IndividualType.JOB_MANAGER },
                                            { label: 'Clearance Agent', value: IndividualType.CLEARANCE_AGENT },
                                        ]}
                                        onChange={(values) => {
                                            setUserTypes(values as IndividualType[]);
                                            setPaginator((prev) => ({ ...prev, page: 0 }));
                                        }}
                                        value={userTypes}
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
                <IndividualsTable
                    handleUpdateStatus={handleUpdateStatus}
                    handleUpdateRole={handleUpdateRole}
                    onEdit={handleEdit}
                    onDetails={handleDetails}
                    onDelete={handleDelete}
                    onResendInvite={handleResendInvite}
                    data={individuals}
                    loading={isLoading}
                    roleSelectOptions={roleSelectOptions}
                    onChange={onIndividualsTableChange}
                />
            </Container>

            <CustomModal
                title={
                    deleteIds.length > 1 ? (
                        <H5>Do you want to remove these users?</H5>
                    ) : (
                        <H5>Do you want to remove this user?</H5>
                    )
                }
                centered
                open={deleteModalVisible}
                onOk={deleteIndividuals}
                onCancel={() => setDeleteModalVisible(false)}
                width={762}
                contentText={
                    deleteIds.length > 1
                        ? 'If you remove all these users, you will lose all the information associated to them.'
                        : 'If you remove this user, you will lose all the information associated with him.'
                }
            />
            <Drawer title="User Details" width="627" open={showUserDetails} onClose={clearIndividualDetailsData}>
                {individual && <IndividualDetails individual={individual} />}
            </Drawer>

            <ConfirmTeamRoleChangeModal
                open={showRoleConfirm}
                onClose={() => {
                    setShowRoleConfirm(false);
                    setPendingRoleChange(null);
                }}
                onConfirm={async () => {
                    if (!pendingRoleChange) return;

                    const { id, role } = pendingRoleChange;
                    const user = getUser(id);

                    setShowRoleConfirm(false);
                    setPendingRoleChange(null);

                    if (!user) return;

                    try {
                        await editIndividual(id, { ...user, type: role });
                        Notification({
                            type: 'success',
                            messageTitle: 'Role Updated',
                            description: "The user's role has been updated successfully.",
                        });
                        refetch();
                    } catch (err) {
                        const errorMessage = getErrorMessage(err);
                        Notification({
                            type: 'error',
                            messageTitle: 'Update Failed',
                            description: errorMessage || 'We were unable to update the user’s role. Please try again.',
                        });
                    }
                }}
            />
        </>
    );
};
export default UserManagement;
